import React, { useState, useEffect } from 'react';
import { X, Lock, Check, Sparkles, Gift, CheckCircle, Percent } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';

const PaywallModal = ({ isOpen, onClose, title, message }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);

  // Referral kód
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(false);
  const [referralChecking, setReferralChecking] = useState(false);
  const [hasExistingDiscount, setHasExistingDiscount] = useState(false);
  const [existingDiscount, setExistingDiscount] = useState(0);

  // Zkontrolovat existující slevu
  useEffect(() => {
    const checkExistingDiscount = async () => {
      if (!user?.id || !isOpen) return;
      try {
        const discount = await api.getCheckoutDiscount(user.id);
        if (discount.discount > 0) {
          setHasExistingDiscount(true);
          setExistingDiscount(discount.discount);
        }
      } catch (error) {
        console.log('No existing discount');
      }
    };
    checkExistingDiscount();
  }, [user?.id, isOpen]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(false);
      return;
    }
    setReferralChecking(true);
    try {
      const result = await api.validateReferralCode(code);
      setReferralValid(result.valid);
    } catch (error) {
      setReferralValid(false);
    } finally {
      setReferralChecking(false);
    }
  };

  if (!isOpen) return null;

  const plans = [
    { key: '1_month', name: '1 měsíc', price: 249, pricePerMonth: 249 },
    { key: '3_months', name: '3 měsíce', price: 699, pricePerMonth: 233 },
    { key: '6_months', name: '6 měsíců', price: 1290, pricePerMonth: 215, popular: true },
  ];

  const features = [
    'Všechny otázky ze všech kategorií',
    'Simulace přijímacích zkoušek',
    'Flashcards pro rychlé opakování',
    'Podrobné vysvětlení odpovědí',
    'Sledování pokroku',
  ];

  const handleSubscribe = async (plan) => {
    if (!user) {
      navigate('/register', { state: { redirectTo: '/pricing' } });
      return;
    }

    setLoading(plan.key);
    try {
      // Pokud je platný referral kód, nejdřív ho aplikuj
      if (referralValid && referralCode) {
        await api.applyReferralCode(referralCode, user.id);
      }

      console.log('Creating checkout session for:', plan.key, user.id, user.email);
      const response = await api.createCheckoutSession(
        plan.key,
        user.id,
        user.email
      );
      console.log('Checkout response:', response);
      if (response.url) {
        window.location.href = response.url;
      } else if (response.error) {
        console.error('Checkout error:', response.error);
        alert('Chyba při vytváření platby: ' + response.error);
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Nepodařilo se vytvořit platební session. Zkuste to prosím znovu.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="glass rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 glass p-4 border-b border-dark-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
              <Lock size={20} className="text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{title || 'Premium obsah'}</h2>
              <p className="text-dark-400 text-sm">{message || 'Pro přístup je potřeba předplatné'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-dark-700 rounded-lg">
            <X size={20} className="text-dark-400" />
          </button>
        </div>

        <div className="p-6">
          {/* Features */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">Co získáš s Premium?</h3>
            <ul className="space-y-2">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Check size={12} className="text-green-400" />
                  </div>
                  <span className="text-dark-300 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Discount Banner - pokud má uživatel existující slevu */}
          {user && hasExistingDiscount && (
            <div className="mb-6 p-3 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Percent size={16} className="text-green-400" />
              </div>
              <div>
                <p className="text-green-400 font-medium text-sm">Máš aktivní slevu {existingDiscount}%!</p>
                <p className="text-dark-400 text-xs">Sleva se automaticky aplikuje při platbě</p>
              </div>
            </div>
          )}

          {/* Referral Code Input - pouze pro přihlášené bez existující slevy */}
          {user && !hasExistingDiscount && (
            <div className="mb-6 p-4 rounded-xl bg-dark-800/50 border border-dark-700">
              <label className="block text-dark-300 text-sm font-medium mb-2">
                Pozval tě kamarád? Zadej kód z pozvánky.
              </label>
              <div className="relative">
                <Gift size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-500" />
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => {
                    const code = e.target.value.toUpperCase();
                    setReferralCode(code);
                    if (code.length >= 4) {
                      validateReferralCode(code);
                    } else {
                      setReferralValid(false);
                    }
                  }}
                  placeholder="Zadej kód"
                  className={`w-full pl-9 pr-9 py-2 rounded-lg bg-dark-900 border text-white placeholder-dark-500 focus:outline-none transition-colors text-sm font-mono tracking-wider ${
                    referralValid ? 'border-green-500' : 'border-dark-600 focus:border-primary-500'
                  }`}
                />
                {referralChecking && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-500"></div>
                  </div>
                )}
                {!referralChecking && referralValid && (
                  <CheckCircle size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-400" />
                )}
              </div>
              {referralValid && (
                <p className="text-green-400 text-xs mt-2">Získáš 10% slevu na první předplatné!</p>
              )}
            </div>
          )}

          {/* Plans */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {plans.map((plan) => (
              <div
                key={plan.key}
                className={`relative rounded-xl p-4 ${
                  plan.popular
                    ? 'bg-gradient-to-b from-primary-900/50 to-dark-800 border-2 border-primary-500'
                    : 'glass border border-dark-700'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full gradient-bg text-white text-xs font-semibold">
                      <Sparkles size={10} />
                      <span>Nejlepší</span>
                    </div>
                  </div>
                )}

                <div className="text-center mb-3">
                  <h4 className="text-white font-semibold">{plan.name}</h4>
                  <div className="flex items-baseline justify-center gap-1 mt-1">
                    <span className="text-2xl font-bold text-white">{plan.price}</span>
                    <span className="text-dark-400 text-sm">Kč</span>
                  </div>
                  <p className="text-dark-500 text-xs mt-1">
                    {plan.pricePerMonth} Kč/měsíc
                  </p>
                </div>

                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={loading !== null}
                  className={`w-full py-2 rounded-lg font-semibold text-sm transition-all ${
                    plan.popular
                      ? 'gradient-bg text-white hover:opacity-90'
                      : 'bg-dark-700 text-white hover:bg-dark-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading === plan.key ? 'Načítání...' : 'Vybrat'}
                </button>
              </div>
            ))}
          </div>

          {/* Close button */}
          <div className="mt-6 text-center">
            <button
              onClick={onClose}
              className="text-dark-400 hover:text-white text-sm transition-colors"
            >
              Pokračovat s omezeným přístupem
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
